import { useRef, useState } from 'react';
import { Camera, Check, Loader2, WifiOff, Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { captureAndUpload, tryGetLocation } from '@/lib/construction-offline';
import { getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface PhotoCaptureProps {
  /** URL path under /api/v1/construction-photos/, e.g. `/projects/abc/bus` */
  endpoint: string;
  /** Form fields to send alongside the file */
  extraFields?: Record<string, string>;
  /** Button label */
  label?: string;
  /** Compact icon-only button */
  iconOnly?: boolean;
  /** Indicates already-captured state */
  done?: boolean;
  /** Called after successful upload OR queueing */
  onCaptured?: (result: { status: 'sent' | 'queued'; result?: any; id?: string }) => void;
  /** Disable until enabled (e.g. busIn must happen before busOut) */
  disabled?: boolean;
  /** Auto-attempt GPS (default true) */
  withGps?: boolean;
}

export function PhotoCapture({
  endpoint,
  extraFields = {},
  label = 'Photo',
  iconOnly = false,
  done = false,
  onCaptured,
  disabled = false,
  withGps = true,
}: PhotoCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  async function handleFile(file: File) {
    setBusy(true);
    setStatusMsg(null);
    try {
      const fields: Record<string, string> = {
        ...extraFields,
        takenAt: new Date().toISOString(),
      };
      if (withGps) {
        const gps = await tryGetLocation();
        if (gps) {
          fields.lat = String(gps.lat);
          fields.lng = String(gps.lng);
        }
      }
      const jwt = getStoredToken();
      if (!jwt) {
        setStatusMsg('Not logged in');
        setBusy(false);
        return;
      }
      const result = await captureAndUpload({
        endpoint: `/api/v1/construction-photos${endpoint}`,
        fields,
        blob: file,
        baseUrl: API_BASE,
        jwt,
      });
      if (result.status === 'sent') {
        setStatusMsg('Uploaded');
      } else {
        setStatusMsg('Queued (offline)');
      }
      onCaptured?.(result);
    } catch (e: any) {
      setStatusMsg(e?.message ?? 'Failed');
    } finally {
      setBusy(false);
      setTimeout(() => setStatusMsg(null), 2500);
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';  // allow re-pick same file
        }}
      />
      <Button
        type="button"
        variant={done ? 'default' : 'outline'}
        size={iconOnly ? 'icon' : 'sm'}
        disabled={disabled || busy}
        onClick={() => inputRef.current?.click()}
        className="gap-2"
        title={statusMsg ?? label}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> :
         done ? <Check className="h-4 w-4" /> :
         statusMsg === 'Queued (offline)' ? <WifiOff className="h-4 w-4" /> :
         statusMsg === 'Uploaded' ? <Check className="h-4 w-4" /> :
         <Camera className="h-4 w-4" />}
        {!iconOnly && <span>{statusMsg ?? (done ? 'Done' : label)}</span>}
      </Button>
    </>
  );
}

interface QueueIndicatorProps { count: number; onDrain: () => void; busy: boolean; }
export function QueueIndicator({ count, onDrain, busy }: QueueIndicatorProps) {
  if (count === 0) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
      <WifiOff className="h-4 w-4" />
      <span>{count} photo{count > 1 ? 's' : ''} queued (offline)</span>
      <Button size="sm" variant="ghost" onClick={onDrain} disabled={busy} className="ms-auto h-7 gap-1">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
        Try upload
      </Button>
    </div>
  );
}
